/**
 * Timezone-aware display formatting for the admin web.
 *
 * Attendance is an operational fact anchored to the **worksite's** timezone, not
 * the viewer's laptop: a check-in at 09:00 in Kuala Lumpur must read 09:00 for
 * every admin, whether their machine is on MYT, UTC or anything else. So every
 * tenant-facing timestamp is rendered in the tenant's configured timezone
 * (`Settings → Timezone`, the same value the backend computes "today" from).
 *
 * Pass the tz explicitly — `useTenantTimezone()` supplies it in components.
 * The platform (super-admin) console is deliberately excluded: it spans many
 * tenants, so no single tenant timezone applies there.
 */

/** The viewer's own timezone — the fallback until tenant settings load. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "30/07/2026" — date only, in `tz`. */
export function formatDateIn(
  value: string | Date | null | undefined,
  tz: string,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, dateStyle: "short" }).format(d);
}

/** "16:09" — 24-hour clock, in `tz`. Used for check-in / check-out times. */
export function formatTimeIn(
  value: string | Date | null | undefined,
  tz: string,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** "30/07/2026, 16:09" — date + time, in `tz`. */
export function formatDateTimeIn(
  value: string | Date | null | undefined,
  tz: string,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    dateStyle: "short",
    timeStyle: "short",
    hour12: false,
  }).format(d);
}

/**
 * Calendar date in `tz` as YYYY-MM-DD — the wire format the attendance and
 * report endpoints filter on. Mirrors the backend's `dateInTz`.
 */
export function isoDateIn(value: string | Date, tz: string): string {
  const d = toDate(value);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/**
 * "Monday" for a YYYY-MM-DD calendar date (the export's Week column,
 * FEAT-2026-029). Read as a UTC day: a date has no timezone, and parsing it
 * as local midnight moves it to the day before for anyone west of UTC.
 */
export function weekdayName(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return "";
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "long" }).format(d);
}

/**
 * "Today, 16:09" / "30/07/2026 16:09" — relative-ish label for activity feeds.
 * "Today" is judged in `tz`, so it flips at tenant midnight, not the viewer's.
 */
export function formatDayTimeIn(
  value: string | Date | null | undefined,
  tz: string,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  const time = formatTimeIn(d, tz);
  return isoDateIn(d, tz) === isoDateIn(new Date(), tz)
    ? `Today, ${time}`
    : `${formatDateIn(d, tz)} ${time}`;
}

/** A short label for the zone itself, e.g. "GMT+8" — for disambiguating headers. */
export function timeZoneLabel(tz: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
}
